// src/pages/Financeiro/Dashboard/DashboardContas.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Typography, useTheme, alpha, Card, CardContent, ButtonGroup, Button, Chip, Pagination,
  useMediaQuery,
  Container,
  Popover,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart, Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssessmentIcon from '@mui/icons-material/Assessment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';

import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import { useGraficoResponsivo } from '../../../shared/components/graficos/useGraficoResponsivo';
import {
  GraficosContasService,
  IDashboardContasGraficos,
  IGraficoContasItem,
  ITopClientePago,
  IClienteAtrasado,
  IDRE,
  TPeriodoContas,
} from '../../../shared/services/api/Financeiro/GraficosContasService';

dayjs.locale('pt-br');
dayjs.extend(utc);

type TModoContas = 'realizado' | 'previsto' | 'dre';

interface PontoContas extends IGraficoContasItem {
  label: string;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatQuantity = (value: number) => {
  return value.toLocaleString('pt-BR');
};

interface ICustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: PontoContas }>;
  label?: string;
  modo?: TModoContas;
}

const CustomTooltip: React.FC<ICustomTooltipProps> = ({ active, payload, label, modo = 'realizado' }) => {
  const theme = useTheme();

  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload as PontoContas;

  const valorSaida = modo === 'realizado' ? item.valorpago : item.valorapagar;
  const qtdeSaida = modo === 'realizado' ? item.qtdepago : item.qtdeapagar;
  const valorEntrada = modo === 'realizado' ? item.valorrecebido : item.valorareceber;
  const qtdeEntrada = modo === 'realizado' ? item.qtderecebido : item.qtdeareceber;
  const saldo = modo === 'realizado' ? item.saldorealizado : item.saldoprevisto;

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        boxShadow: theme.shadows[4],
        minWidth: 190,
      }}
    >
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.error.dark }}>
          {modo === 'realizado' ? 'Pago' : 'A Pagar'}
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(valorSaida)} ({qtdeSaida})
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="caption" sx={{ color: theme.palette.success.dark }}>
          {modo === 'realizado' ? 'Recebido' : 'A Receber'}
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {formatCurrency(valorEntrada)} ({qtdeEntrada})
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        mt={0.5}
        pt={0.5}
        borderTop={`1px solid ${alpha(theme.palette.divider, 0.2)}`}
      >
        <Typography variant="caption" fontWeight="bold">
          Saldo
        </Typography>
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{ color: saldo >= 0 ? theme.palette.success.dark : theme.palette.error.dark }}
        >
          {formatCurrency(saldo)}
        </Typography>
      </Box>
    </Paper>
  );
};

interface IItemRankingClientes {
  idparceiro: string;
  nome: string;
  valor: number;
  quantidade: number;
  subtitulo?: string;
}

interface IRankingClientesProps {
  titulo: string;
  icon: React.ReactNode;
  cor: string;
  itens: IItemRankingClientes[];
  vazio: string;
}

const ITENS_POR_PAGINA = 5;

const RankingClientes: React.FC<IRankingClientesProps> = ({ titulo, icon, cor, itens, vazio }) => {
  const theme = useTheme();
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setPagina(1);
  }, [itens]);

  const maiorValor = itens[0]?.valor || 1;

  const totalPaginas = Math.max(1, Math.ceil(itens.length / ITENS_POR_PAGINA));
  const itensPagina = itens.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA);

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        overflow: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        pb={1}
        borderBottom={`2px solid ${alpha(cor, 0.2)}`}
      >
        {icon}

        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ flex: 1 }}
        >
          {titulo}
        </Typography>

        <Chip
          label={`${itens.length} clientes`}
          size="small"
          sx={{ bgcolor: alpha(cor, 0.12), color: cor, fontWeight: 'bold' }}
          variant="outlined"
        />
      </Box>

      {itens.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {vazio}
          </Typography>
        </Box>
      )}

      {itensPagina.map((item, index) => (
        <Box
          key={item.idparceiro}
          sx={{
            p: 1.5,
            borderRadius: 2,
            mb: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.4),
            transition: 'all 0.2s',
            cursor: 'default',
            '&:hover': {
              bgcolor: alpha(cor, 0.08),
              transform: 'translateX(4px)',
            },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5} flex={1} sx={{ minWidth: 0 }}>
              <Chip
                label={`#${(pagina - 1) * ITENS_POR_PAGINA + index + 1}`}
                size="small"
                sx={{ fontWeight: 'bold', minWidth: 40, bgcolor: cor, color: '#fff' }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  noWrap
                  title={item.nome ?? undefined}
                >
                  {item.nome || 'Cliente não identificado'}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {item.subtitulo || `${item.quantidade} parcela(s)`}
                </Typography>
              </Box>
            </Box>

            <Box textAlign="right" sx={{ flexShrink: 0, pl: 1 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: cor }}>
                {formatCurrency(item.valor)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1,
              height: 3,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.grey[500], 0.2),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${(item.valor / (maiorValor || 1)) * 100}%`,
                height: '100%',
                bgcolor: cor,
                borderRadius: 1,
                transition: 'width 0.3s',
              }}
            />
          </Box>
        </Box>
      ))}

      {itens.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt="auto"
          pt={1}
          borderTop={`1px solid ${alpha(theme.palette.divider, 0.1)}`}
        >
          <Pagination
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            count={totalPaginas}
            size="small"
            color="primary"
          />
        </Box>
      )}
    </Paper>
  );
};

interface ILinhaDREProps {
  label: string;
  valor: number;
  quantidade?: number;
  nivel?: number;
  destaque?: boolean;
  cor?: string;
}

const LinhaDRE: React.FC<ILinhaDREProps> = ({ label, valor, quantidade, nivel = 0, destaque = false, cor }) => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: destaque ? 1.2 : 0.7,
        pl: nivel * 3,
        mt: destaque ? 1.5 : 0,
        borderTop: destaque ? `2px solid ${alpha(theme.palette.divider, 0.25)}` : undefined,
      }}
    >
      <Typography
        variant={destaque ? 'subtitle2' : 'body2'}
        fontWeight={destaque ? 'bold' : 500}
        color={nivel > 0 ? 'text.secondary' : 'text.primary'}
      >
        {label}
      </Typography>

      <Box textAlign="right">
        <Typography
          variant={destaque ? 'subtitle1' : 'body2'}
          fontWeight="bold"
          sx={{ color: cor }}
        >
          {formatCurrency(valor)}
        </Typography>

        {quantidade !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {quantidade} {quantidade === 1 ? 'lançamento' : 'lançamentos'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface IPainelDREProps {
  dre: IDRE;
}

const PainelDRE: React.FC<IPainelDREProps> = ({ dre }) => {
  const theme = useTheme();
  const maiorDespesa = dre.despesas[0]?.valor || 1;

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        pb={1}
        borderBottom={`2px solid ${alpha(theme.palette.divider, 0.15)}`}
      >
        <AssessmentIcon sx={{ color: theme.palette.text.secondary }} />

        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
          Regime de competência — receita e despesas pela data de emissão
        </Typography>
      </Box>

      <LinhaDRE label="Receita Bruta de Vendas" valor={dre.receitabruta} quantidade={dre.qtdevendas} />

      <LinhaDRE
        label="(-) Devoluções"
        valor={-dre.devolucoes}
        quantidade={dre.qtdedevolucoes}
        cor={theme.palette.warning.main}
      />

      <LinhaDRE
        label="(=) Receita Líquida"
        valor={dre.receitaliquida}
        destaque
        cor={theme.palette.success.main}
      />

      <Box mt={2.5} mb={1}>
        <Typography variant="body2" fontWeight="bold" color="text.secondary">
          (-) Despesas Operacionais
        </Typography>
      </Box>

      {dre.despesas.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
          Nenhuma despesa lançada no período
        </Typography>
      )}

      {dre.despesas.map((item) => (
        <Box key={item.categoria} sx={{ pl: 3, py: 0.7 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {item.categoria}
            </Typography>

            <Box textAlign="right">
              <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.error.dark }}>
                {formatCurrency(item.valor)}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {item.quantidade} {item.quantidade === 1 ? 'lançamento' : 'lançamentos'}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 0.5,
              height: 3,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.grey[500], 0.2),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${(item.valor / maiorDespesa) * 100}%`,
                height: '100%',
                bgcolor: theme.palette.error.main,
                borderRadius: 1,
                transition: 'width 0.3s',
              }}
            />
          </Box>
        </Box>
      ))}

      <LinhaDRE
        label="(=) Total de Despesas Operacionais"
        valor={-dre.despesastotal}
        destaque
        cor={theme.palette.error.main}
      />

      <LinhaDRE
        label="(=) Resultado do Período"
        valor={dre.resultado}
        destaque
        cor={dre.resultado >= 0 ? theme.palette.success.main : theme.palette.error.main}
      />
    </Box>
  );
};

export const DashboardContas: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const graficoResp = useGraficoResponsivo();
  // Celular: cards compactos (menos respiro), ver CARDS abaixo.
  const cardConteudoSx = isMobile ? { p: 1.25, '&:last-child': { pb: 1.25 } } : {};

  const [isLoading, setIsLoading] = useState(false);
  const [msgType, setMsgType] = useState<'success' | 'error' | 'warning' | 'info'>('error');
  const [msg, setMsg] = useState('');
  const [openMsg, setOpenMsg] = useState(false);

  const [periodo, setPeriodo] = useState<TPeriodoContas>('atual');
  const [modo, setModo] = useState<TModoContas>('realizado');
  const [ano, setAno] = useState<number>(dayjs().year());
  const [mes, setMes] = useState<number>(dayjs().month() + 1);

  const [anchorPeriodo, setAnchorPeriodo] = useState<HTMLElement | null>(null);
  const [anoPopover, setAnoPopover] = useState(dayjs().year());
  const [mesPopover, setMesPopover] = useState(dayjs().month() + 1);

  const [dados, setDados] = useState<IDashboardContasGraficos | null>(null);

  const nomesMeses = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const nome = dayjs().month(i).format('MMM').replace('.', '');
    return nome.charAt(0).toUpperCase() + nome.slice(1);
  }), []);

  // Setas do card Período: anda um mês (mensal) ou um ano (anual). Do "Mês
  // Atual" passa pro modo mensal, a partir do mês corrente.
  const navegarPeriodo = (passo: -1 | 1) => {
    if (periodo === 'anual') {
      setAno((valor) => valor + passo);
      return;
    }
    const base = periodo === 'mensal' ? dayjs(new Date(ano, mes - 1, 1)) : dayjs().startOf('month');
    const destino = base.add(passo, 'month');
    setPeriodo('mensal');
    setAno(destino.year());
    setMes(destino.month() + 1);
  };

  const abrirSeletorPeriodo = (target: HTMLElement) => {
    if (periodo === 'atual') return;

    setAnoPopover(ano);
    setMesPopover(mes);
    setAnchorPeriodo(target);
  };

  useEffect(() => {
    const carregarDashboard = async () => {
      setIsLoading(true);

      const result = await GraficosContasService.getDashboardContasGraficos(
        periodo,
        periodo !== 'atual' ? ano : undefined,
        periodo === 'mensal' ? mes : undefined,
      );

      setIsLoading(false);

      if (result instanceof Error) {
        setMsgType('error');
        setMsg(result.message);
        setOpenMsg(true);
      } else {
        setDados(result);
      }
    };

    carregarDashboard();
  }, [periodo, ano, mes]);

  const totais = dados?.totais ?? {
    valorpago: 0,
    qtdepago: 0,
    valorrecebido: 0,
    qtderecebido: 0,
    saldorealizado: 0,
    valorapagar: 0,
    qtdeapagar: 0,
    valorareceber: 0,
    qtdeareceber: 0,
    saldoprevisto: 0,
  };

  const dre = dados?.dre ?? {
    receitabruta: 0,
    qtdevendas: 0,
    devolucoes: 0,
    qtdedevolucoes: 0,
    receitaliquida: 0,
    despesas: [],
    despesastotal: 0,
    resultado: 0,
  };

  const intervaloFormatado = useMemo(() => {
    if (!dados?.intervalo) return '';

    return `${dayjs(dados.intervalo.datainicial).format('DD/MM/YY')} — ${dayjs(dados.intervalo.datafinal).format('DD/MM/YY')}`;
  }, [dados?.intervalo]);

  const chartData = useMemo<PontoContas[]>(() => {
    const agrupamento = dados?.agrupamento ?? (periodo === 'anual' ? 'mes' : 'dia');

    return (dados?.grafico ?? []).map((item) => ({
      ...item,
      label: agrupamento === 'dia'
        ? dayjs.utc(item.data).format('DD/MM')
        : dayjs.utc(item.data).format('MMM/YY'),
    }));
  }, [dados?.grafico, dados?.agrupamento, periodo]);

  const rotuloSaida = modo === 'dre' ? 'Despesas Operacionais' : modo === 'realizado' ? 'Pago' : 'A Pagar';
  const rotuloEntrada = modo === 'dre' ? 'Receita Líquida' : modo === 'realizado' ? 'Recebido' : 'A Receber';
  const valorSaida = modo === 'dre' ? dre.despesastotal : modo === 'realizado' ? totais.valorpago : totais.valorapagar;
  const qtdeSaida = modo === 'dre'
    ? dre.despesas.reduce((soma, item) => soma + item.quantidade, 0)
    : modo === 'realizado' ? totais.qtdepago : totais.qtdeapagar;
  const valorEntrada = modo === 'dre' ? dre.receitaliquida : modo === 'realizado' ? totais.valorrecebido : totais.valorareceber;
  const qtdeEntrada = modo === 'dre' ? dre.qtdevendas : modo === 'realizado' ? totais.qtderecebido : totais.qtdeareceber;
  const saldo = modo === 'dre' ? dre.resultado : modo === 'realizado' ? totais.saldorealizado : totais.saldoprevisto;
  const unidadeContagem = modo === 'dre' ? 'lançamento(s)' : 'parcela(s)';
  const dataKeySaida = modo === 'realizado' ? 'valorpago' : 'valorapagar';
  const dataKeyEntrada = modo === 'realizado' ? 'valorrecebido' : 'valorareceber';

  const dadosRosca = useMemo(() => ([
    { name: rotuloEntrada, value: qtdeEntrada, valor: valorEntrada, color: theme.palette.success.dark },
    { name: rotuloSaida, value: qtdeSaida, valor: valorSaida, color: theme.palette.error.dark },
  ]), [rotuloEntrada, rotuloSaida, qtdeEntrada, qtdeSaida, valorEntrada, valorSaida, theme]);

  const totalValorRosca = dadosRosca.reduce((acc, item) => acc + item.valor, 0);

  const topClientesPago = useMemo<IItemRankingClientes[]>(() => (
    (dados?.topClientesPago ?? []).map((item: ITopClientePago) => ({
      idparceiro: item.idparceiro,
      nome: item.nome,
      valor: item.valor,
      quantidade: item.quantidade,
    }))
  ), [dados?.topClientesPago]);

  const clientesAtrasados = useMemo<IItemRankingClientes[]>(() => (
    (dados?.clientesAtrasados ?? []).map((item: IClienteAtrasado) => ({
      idparceiro: item.idparceiro,
      nome: item.nome,
      valor: item.valor,
      quantidade: item.quantidade,
      subtitulo: `${item.quantidade} parcela(s) · venc. mais antigo ${dayjs.utc(item.vencimentomaisantigo).format('DD/MM/YYYY')}`,
    }))
  ), [dados?.clientesAtrasados]);

  return (
    <LayoutBaseDeDashboard
      barraDeFerramentas={<Box />}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: 'transparent',
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          {/* CARDS */}
          <Grid
            container
            spacing={isMobile ? 1 : 2}
            sx={{
              width: '100%',
              m: 0,
              mb: 2,
            }}
          >
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                onClick={(e) => abrirSeletorPeriodo(e.currentTarget)}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  cursor: periodo !== 'atual' ? 'pointer' : 'default',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': periodo !== 'atual' ? {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  } : undefined,
                }}
              >
                <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', ...cardConteudoSx }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Período
                      </Typography>

                      <Box display="flex" alignItems="center" sx={{ mt: isMobile ? 0.25 : 1, ml: -0.75 }}>
                        {/* Setas: mês/ano anterior e seguinte (ver navegarPeriodo) —
                            o clique nelas não abre o seletor do card. */}
                        <Tooltip title={periodo === 'anual' ? 'Ano anterior' : 'Mês anterior'}>
                          <IconButton
                            size="small"
                            aria-label="período anterior"
                            onClick={(e) => { e.stopPropagation(); navegarPeriodo(-1); }}
                            sx={{ p: 0.25, color: theme.palette.info.dark }}
                          >
                            <ChevronLeftIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          noWrap
                          sx={{ lineHeight: 1.2, fontSize: isMobile ? '1rem' : undefined, mx: 0.25 }}
                        >
                          {periodo === 'atual'
                            ? 'Mês Atual'
                            : periodo === 'mensal'
                              ? `${nomesMeses[mes - 1]}/${ano}`
                              : ano}
                        </Typography>

                        {periodo !== 'atual' && (
                          <ArrowDropDownIcon
                            sx={{ color: theme.palette.info.dark, ml: -0.5 }}
                          />
                        )}
                        <Tooltip title={periodo === 'anual' ? 'Próximo ano' : 'Próximo mês'}>
                          <IconButton
                            size="small"
                            aria-label="próximo período"
                            onClick={(e) => { e.stopPropagation(); navegarPeriodo(1); }}
                            sx={{ p: 0.25, color: theme.palette.info.dark }}
                          >
                            <ChevronRightIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {intervaloFormatado && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {intervaloFormatado}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        p: isMobile ? 0.75 : 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <CalendarTodayIcon
                        sx={{
                          fontSize: isMobile ? 20 : 32,
                          color: theme.palette.info.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Popover
                open={Boolean(anchorPeriodo)}
                anchorEl={anchorPeriodo}
                onClose={() => setAnchorPeriodo(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box sx={{ width: periodo === 'mensal' ? 260 : 220, p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((valor) => valor - 1)}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="subtitle2" fontWeight="bold">
                      {anoPopover}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => setAnoPopover((valor) => valor + 1)}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {periodo === 'mensal' && (
                    <Grid container spacing={0.5} sx={{ mb: 1.5 }}>
                      {nomesMeses.map((nome, index) => {
                        const numeroMes = index + 1;

                        return (
                          <Grid item xs={4} key={numeroMes}>
                            <Button
                              fullWidth
                              size="small"
                              variant={mesPopover === numeroMes ? 'contained' : 'outlined'}
                              onClick={() => setMesPopover(numeroMes)}
                              sx={{ minWidth: 0, px: 0.5 }}
                            >
                              {nome}
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}

                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setAno(anoPopover);
                      if (periodo === 'mensal') setMes(mesPopover);
                      setAnchorPeriodo(null);
                    }}
                  >
                    {periodo === 'mensal'
                      ? `Filtrar ${nomesMeses[mesPopover - 1]}/${anoPopover}`
                      : `Filtrar ${anoPopover}`}
                  </Button>
                </Box>
              </Popover>
            </Grid>

            <Grid item xs={6} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent sx={cardConteudoSx}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total {rotuloSaida}
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: isMobile ? 0.25 : 1, fontSize: isMobile ? '1.05rem' : undefined, lineHeight: 1.25, whiteSpace: 'nowrap', color: theme.palette.error.dark }}
                      >
                        {formatCurrency(valorSaida)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {qtdeSaida} {unidadeContagem}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        // Celular: card pela metade da largura — o valor fica com o espaço todo.
                        display: isMobile ? 'none' : undefined,
                        p: isMobile ? 0.75 : 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                      }}
                    >
                      <PaymentsIcon
                        sx={{
                          fontSize: isMobile ? 20 : 32,
                          color: theme.palette.error.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent sx={cardConteudoSx}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        Total {rotuloEntrada}
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: isMobile ? 0.25 : 1, fontSize: isMobile ? '1.05rem' : undefined, lineHeight: 1.25, whiteSpace: 'nowrap', color: theme.palette.success.dark }}
                      >
                        {formatCurrency(valorEntrada)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {qtdeEntrada} {unidadeContagem}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        // Celular: card pela metade da largura — o valor fica com o espaço todo.
                        display: isMobile ? 'none' : undefined,
                        p: isMobile ? 0.75 : 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                      }}
                    >
                      <AccountBalanceWalletIcon
                        sx={{
                          fontSize: isMobile ? 20 : 32,
                          color: theme.palette.success.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <CardContent sx={cardConteudoSx}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        {modo === 'dre' ? 'Resultado do Período' : 'Saldo'}
                      </Typography>

                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mt: isMobile ? 0.25 : 1, fontSize: isMobile ? '1.05rem' : undefined, lineHeight: 1.25, whiteSpace: 'nowrap', color: saldo >= 0 ? theme.palette.success.dark : theme.palette.error.dark }}
                      >
                        {formatCurrency(saldo)}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {modo === 'dre'
                          ? 'Receita Líquida − Despesas'
                          : modo === 'realizado' ? 'Recebido − Pago' : 'A Receber − A Pagar'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: isMobile ? 0.75 : 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                      }}
                    >
                      <SummarizeIcon
                        sx={{
                          fontSize: isMobile ? 20 : 32,
                          color: theme.palette.success.dark
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* GRAFICOS */}
          <Grid
            container
            spacing={isMobile ? 1 : 2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} lg={9} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: graficoResp.respiro,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                  >
                    {modo === 'dre' ? 'DRE — Demonstrativo de Resultado' : `Contas ${rotuloSaida} x ${rotuloEntrada}`}
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <ButtonGroup size="small" sx={graficoResp.grupoBotoes}>
                      <Button
                        variant={periodo === 'atual' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('atual')}
                      >
                        Atual
                      </Button>

                      <Button
                        variant={periodo === 'mensal' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('mensal')}
                      >
                        Mensal
                      </Button>

                      <Button
                        variant={periodo === 'anual' ? 'contained' : 'outlined'}
                        onClick={() => setPeriodo('anual')}
                      >
                        Anual
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="small" sx={graficoResp.grupoBotoes}>
                      <Button
                        variant={modo === 'realizado' ? 'contained' : 'outlined'}
                        onClick={() => setModo('realizado')}
                      >
                        Pago / Recebido
                      </Button>

                      <Button
                        variant={modo === 'previsto' ? 'contained' : 'outlined'}
                        onClick={() => setModo('previsto')}
                      >
                        A Pagar / A Receber
                      </Button>

                      <Button
                        variant={modo === 'dre' ? 'contained' : 'outlined'}
                        onClick={() => setModo('dre')}
                      >
                        DRE
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Box>

                {modo !== 'dre' && (
                <Box
                  sx={{
                    width: '100%',
                    height: graficoResp.altura(360),
                    position: 'relative',
                  }}
                >
                  {isLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  )}

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <ComposedChart margin={graficoResp.margem} data={chartData}>
                      <defs>
                        <linearGradient
                          id="gradientEntradaContas"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.7}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>

                        <linearGradient
                          id="gradientSaidaContas"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.error.main}
                            stopOpacity={0.7}
                          />

                          <stop
                            offset="95%"
                            stopColor={theme.palette.error.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid {...graficoResp.grade} strokeDasharray="3 3" />

                      <XAxis {...graficoResp.eixoX} dataKey="label" />

                      <YAxis {...graficoResp.eixoY}
                        tickFormatter={(value) =>
                          value.toLocaleString('pt-BR', {
                            notation: 'compact',
                          })
                        }
                      />

                      <RechartsTooltip {...graficoResp.dica} content={<CustomTooltip modo={modo} />} />

                      <Legend {...graficoResp.legenda} />

                      <Area
                        type="monotone"
                        dataKey={dataKeyEntrada}
                        name={rotuloEntrada}
                        stroke={theme.palette.success.main}
                        fill="url(#gradientEntradaContas)"
                        strokeWidth={2}
                      />

                      <Area
                        type="monotone"
                        dataKey={dataKeySaida}
                        name={rotuloSaida}
                        stroke={theme.palette.error.main}
                        fill="url(#gradientSaidaContas)"
                        strokeWidth={2}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
                )}

                {modo === 'dre' && <PainelDRE dre={dre} />}
              </Paper>
            </Grid>

            {/* GRAFICO ROSCA */}
            {modo !== 'dre' && (
            <Grid item xs={12} lg={3} sx={{ minWidth: 0 }}>
              <Paper
                sx={{
                  p: graficoResp.respiro,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={2}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                  >
                    {rotuloEntrada} x {rotuloSaida}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <PieChart>
                      <Pie {...graficoResp.pizza}
                        data={dadosRosca}
                        dataKey="valor"
                        innerRadius={35}
                        outerRadius={55}
                        paddingAngle={3}
                      >
                        {dadosRosca.map((item, index) => (
                          <Cell
                            key={index}
                            fill={item.color}
                          />
                        ))}
                      </Pie>

                      <RechartsTooltip {...graficoResp.dica}
                        formatter={(value: any, name: any, entry: any) => {
                          const qtd = entry?.payload?.value ?? 0;
                          const percentual = totalValorRosca > 0 ? ((Number(value) / totalValorRosca) * 100).toFixed(1) : '0';

                          return [
                            `${formatCurrency(Number(value))} · ${qtd} parcela(s) · ${percentual}%`,
                            name,
                          ];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box mt={1}>
                  {dadosRosca.map((item) => (
                    <Box
                      key={item.name}
                      sx={{ mb: 1.5 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: item.color,
                            }}
                          />

                          <Typography variant="caption">
                            {item.name}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: item.color }}
                        >
                          {totalValorRosca > 0 ? ((item.valor / totalValorRosca) * 100).toFixed(1) : '0'}%
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 2.25 }}
                      >
                        {item.value} parcela(s) · {formatCurrency(item.valor)}
                      </Typography>
                    </Box>
                  ))}

                  {totalValorRosca === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Nenhum dado encontrado
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
            )}
          </Grid>

          {/* RANKINGS */}
          <Grid
            container
            spacing={2}
            sx={{
              width: '100%',
              m: 0,
              pb: 3,
            }}
          >
            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <RankingClientes
                titulo="Clientes com Maior Valor Pago"
                icon={<GroupsIcon sx={{ color: theme.palette.info.dark }} />}
                cor={theme.palette.info.main}
                itens={topClientesPago}
                vazio="Nenhum pagamento encontrado no período"
              />
            </Grid>

            <Grid item xs={12} lg={6} sx={{ minWidth: 0 }}>
              <RankingClientes
                titulo="Clientes com Contas a Receber Atrasadas"
                icon={<WarningAmberIcon sx={{ color: theme.palette.error.dark }} />}
                cor={theme.palette.error.main}
                itens={clientesAtrasados}
                vazio="Nenhuma conta a receber atrasada"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={() => setOpenMsg(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenMsg(false)} severity={msgType} sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>
    </LayoutBaseDeDashboard>
  );
};
